import { useEffect, useState } from 'react'
import { useNavigate, Link, useParams } from '@tanstack/react-router'
import { ArrowLeft, Clock, Trash2, Plus, Save, Building2, Accessibility } from 'lucide-react'
import { useAuthStore } from '../lib/auth'
import { api, type Building, type CleaningSchedule, type OpeningSchedule, type Room } from '../lib/api'
import { AddressLookup, type AddressData } from '../components/AddressLookup'
import { DashboardLayout } from '../components/DashboardLayout'
import { 
  ACCESSIBILITY_CATEGORY_LABELS, 
  getAccessibilityCategories,
  getAccessibilityFeaturesByCategory,
} from '@roomsi/shared'

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

interface CleaningScheduleInput {
  id: string
  name: string
  dayOfWeek: number
  startTime: string
  endTime: string
  isNew?: boolean
  toDelete?: boolean
}

export function BuildingFormPage() {
  const navigate = useNavigate()
  const params = useParams({ strict: false })
  const buildingId = params.buildingId as string | undefined
  const isEditing = buildingId && buildingId !== 'new'
  
  const { user, currentOrg, isAuthenticated, isLoading: authLoading, checkAuth } = useAuthStore()
  
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [hasChanges, setHasChanges] = useState(false)
  
  // Building details
  const [addressData, setAddressData] = useState<AddressData>({
    name: '',
    address: '',
    city: '',
    postcode: '',
    country: '',
    googleMapsUrl: '',
  })
  const [directions, setDirections] = useState('')
  
  // Accessibility
  const [accessibilityFeatures, setAccessibilityFeatures] = useState<string[]>([])
  const [accessibilityNotes, setAccessibilityNotes] = useState('')
  
  // Opening hours
  const [openingHours, setOpeningHours] = useState<Record<number, { isActive: boolean; openTime: string; closeTime: string }>>(() => {
    const hours: Record<number, { isActive: boolean; openTime: string; closeTime: string }> = {}
    for (let i = 0; i < 7; i++) {
      hours[i] = {
        isActive: i >= 1 && i <= 5, // Mon-Fri
        openTime: '09:00',
        closeTime: '17:00',
      }
    }
    return hours
  })
  
  // Cleaning schedules
  const [cleaningSchedules, setCleaningSchedules] = useState<CleaningScheduleInput[]>([])
  
  // Rooms in building
  const [buildingRooms, setBuildingRooms] = useState<Room[]>([])
  
  useEffect(() => {
    checkAuth()
  }, [])
  
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate({ to: '/login' })
    }
  }, [authLoading, isAuthenticated, navigate])
  
  // Wait for auth to complete before loading data
  useEffect(() => {
    if (!authLoading && isAuthenticated && isEditing && buildingId) {
      loadBuilding()
    }
  }, [authLoading, isAuthenticated, isEditing, buildingId])
  
  const loadBuilding = async () => {
    if (!buildingId) return
    
    setIsLoading(true)
    try {
      // Load building details and opening schedules in parallel
      const [buildingData, openingData] = await Promise.all([
        api.buildings.get(buildingId),
        api.buildings.getOpeningSchedules(buildingId).catch(() => ({ schedules: [] }))
      ])
      
      const building = buildingData.building
      setAddressData({
        name: building.name,
        address: building.address || '',
        city: building.city || '',
        postcode: building.postcode || '',
        country: building.country || '',
        googleMapsUrl: building.googleMapsUrl || '',
      })
      setDirections(building.directions || '')
      setAccessibilityFeatures(building.accessibilityFeatures || [])
      setAccessibilityNotes(building.accessibilityNotes || '')
      
      // Set cleaning schedules
      setCleaningSchedules(buildingData.cleaningSchedules.map(s => ({
        id: s.id,
        name: s.name,
        dayOfWeek: s.dayOfWeek,
        startTime: s.startTime,
        endTime: s.endTime,
      })))
      
      // Set opening hours
      const hours: Record<number, { isActive: boolean; openTime: string; closeTime: string }> = {}
      for (let i = 0; i < 7; i++) {
        const schedule = openingData.schedules.find(s => s.dayOfWeek === i)
        hours[i] = {
          isActive: schedule?.isActive ?? (i >= 1 && i <= 5),
          openTime: schedule?.openTime ?? '09:00',
          closeTime: schedule?.closeTime ?? '17:00',
        }
      }
      setOpeningHours(hours)
      
      // Set rooms
      setBuildingRooms(buildingData.rooms)
      
      setHasChanges(false)
    } catch (err: any) {
      setError(err.message || 'Failed to load building')
    } finally {
      setIsLoading(false)
    }
  }
  
  const handleAddressChange = (newAddress: AddressData) => {
    setAddressData(newAddress)
    setHasChanges(true)
  }
  
  const handleDirectionsChange = (value: string) => {
    setDirections(value)
    setHasChanges(true)
  }
  
  const handleOpeningHourChange = (day: number, field: 'isActive' | 'openTime' | 'closeTime', value: boolean | string) => {
    setOpeningHours(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: value }
    }))
    setHasChanges(true)
  }
  
  const handleAddCleaningSchedule = () => {
    setCleaningSchedules(prev => [...prev, {
      id: `new-${Date.now()}`,
      name: 'Cleaning',
      dayOfWeek: 1,
      startTime: '08:00',
      endTime: '09:00',
      isNew: true,
    }])
    setHasChanges(true)
  }
  
  const handleUpdateCleaningSchedule = (id: string, field: keyof CleaningScheduleInput, value: string | number) => {
    setCleaningSchedules(prev => prev.map(s => 
      s.id === id ? { ...s, [field]: value } : s
    ))
    setHasChanges(true)
  }
  
  const handleDeleteCleaningSchedule = (id: string) => {
    setCleaningSchedules(prev => {
      const schedule = prev.find(s => s.id === id)
      if (schedule?.isNew) {
        // Just remove new schedules
        return prev.filter(s => s.id !== id)
      }
      // Mark existing schedules for deletion
      return prev.map(s => s.id === id ? { ...s, toDelete: true } : s)
    })
    setHasChanges(true)
  }
  
  const handleSave = async () => {
    if (!addressData.name.trim()) {
      setError('Building name is required')
      return
    }
    
    setIsSaving(true)
    setError('')
    
    try {
      let savedBuildingId = buildingId
      
      // Create or update building
      const buildingPayload = {
        name: addressData.name,
        address: addressData.address || undefined,
        city: addressData.city || undefined,
        postcode: addressData.postcode || undefined,
        country: addressData.country || undefined,
        googleMapsUrl: addressData.googleMapsUrl || undefined,
        directions: directions || undefined,
        accessibilityFeatures: accessibilityFeatures.length > 0 ? accessibilityFeatures : undefined,
        accessibilityNotes: accessibilityNotes || undefined,
      }
      
      if (isEditing && buildingId) {
        await api.buildings.update(buildingId, buildingPayload)
      } else {
        const result = await api.buildings.create(buildingPayload)
        savedBuildingId = result.building.id
      }
      
      if (!savedBuildingId) throw new Error('No building ID')
      
      // Save opening hours
      const openingScheduleData = Object.entries(openingHours).map(([day, data]) => ({
        dayOfWeek: parseInt(day),
        openTime: data.openTime,
        closeTime: data.closeTime,
        isActive: data.isActive,
      }))
      await api.buildings.updateOpeningSchedules(savedBuildingId, openingScheduleData)
      
      // Handle cleaning schedules
      for (const schedule of cleaningSchedules) {
        if (schedule.toDelete && !schedule.isNew) {
          await api.buildings.deleteCleaningSchedule(savedBuildingId, schedule.id)
        } else if (schedule.isNew && !schedule.toDelete) {
          await api.buildings.createCleaningSchedule(savedBuildingId, {
            name: schedule.name,
            dayOfWeek: schedule.dayOfWeek,
            startTime: schedule.startTime,
            endTime: schedule.endTime,
          })
        }
        // Note: We don't have an update endpoint, so existing schedules remain as-is
      }
      
      setHasChanges(false)
      navigate({ to: '/dashboard/buildings' })
    } catch (err: any) {
      setError(err.message || 'Failed to save building')
    } finally {
      setIsSaving(false)
    }
  }
  
  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }
  
  if (!isAuthenticated || !user) {
    return null
  }

  const visibleCleaningSchedules = cleaningSchedules.filter(s => !s.toDelete)

  const headerActions = (
    <>
      <Link 
        to="/dashboard/buildings" 
        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
      >
        <ArrowLeft className="w-5 h-5" />
      </Link>
      <div className="flex-1" />
      <button
        onClick={handleSave}
        disabled={isSaving || !addressData.name.trim()}
        className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
      >
        <Save className="w-4 h-4" />
        {isSaving ? 'Saving...' : 'Save Building'}
      </button>
    </>
  )

  return (
    <DashboardLayout title={isEditing ? 'Edit Building' : 'New Building'} actions={headerActions}>
      <div className="max-w-4xl">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}
        
        <div className="space-y-6">
          {/* Building Details */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Building Details</h2>
            
            <div className="space-y-4">
              <AddressLookup
                value={addressData}
                onChange={handleAddressChange}
                  buildingNameLabel="Building Name *"
                  buildingNamePlaceholder="e.g. Main Office, Community Center"
                />
                
                {addressData.city && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      How to Get Here <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <textarea
                      value={directions}
                      onChange={e => handleDirectionsChange(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Enter directions, parking info, access instructions..."
                    />
                  </div>
                )}
              </div>
            </div>
            
            {/* Accessibility Features */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                <Accessibility className="w-5 h-5 inline-block mr-2" />
                Accessibility Features
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Select the accessibility features available at this building. These will be inherited by all rooms unless overridden.
              </p>
              
              {getAccessibilityCategories().map(category => (
                <div key={category} className="mb-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">{ACCESSIBILITY_CATEGORY_LABELS[category]}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {getAccessibilityFeaturesByCategory(category).map(feature => (
                      <label 
                        key={feature.id}
                        className="flex items-start gap-2 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={accessibilityFeatures.includes(feature.id)}
                          onChange={e => {
                            if (e.target.checked) {
                              setAccessibilityFeatures(prev => [...prev, feature.id])
                            } else {
                              setAccessibilityFeatures(prev => prev.filter(id => id !== feature.id))
                            }
                            setHasChanges(true)
                          }}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-0.5"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">{feature.label}</div>
                          <div className="text-xs text-gray-500">{feature.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Accessibility Notes <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={accessibilityNotes}
                  onChange={e => {
                    setAccessibilityNotes(e.target.value)
                    setHasChanges(true)
                  }}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Describe any additional accessibility accommodations, limitations, or contact information for accessibility queries..."
                />
              </div>
            </div>
            
            {/* Opening Hours */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                <Clock className="w-5 h-5 inline-block mr-2" />
                Opening Hours
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Set the default opening hours for this building. Rooms will inherit these hours unless overridden.
              </p>
              
              <div className="space-y-3">
                {DAYS.map((day, i) => (
                  <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                    <label className="flex items-center gap-2 w-28 flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={openingHours[i]?.isActive ?? false}
                        onChange={e => handleOpeningHourChange(i, 'isActive', e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm font-medium text-gray-700">{day}</span>
                    </label>
                    
                    {openingHours[i]?.isActive ? (
                      <div className="flex items-center gap-2 ml-6 sm:ml-0">
                        <input
                          type="time"
                          value={openingHours[i]?.openTime ?? '09:00'}
                          onChange={e => handleOpeningHourChange(i, 'openTime', e.target.value)}
                          className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                        />
                        <span className="text-gray-500">to</span>
                        <input
                          type="time"
                          value={openingHours[i]?.closeTime ?? '17:00'}
                          onChange={e => handleOpeningHourChange(i, 'closeTime', e.target.value)}
                          className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400 ml-6 sm:ml-0">Closed</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            {/* Cleaning Schedules */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Cleaning Schedules</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Block times when rooms are unavailable for cleaning or maintenance.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleAddCleaningSchedule}
                  className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                >
                  <Plus className="w-4 h-4" />
                  Add Schedule
                </button>
              </div>
              
              {visibleCleaningSchedules.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                  <Clock className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">No cleaning schedules</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {visibleCleaningSchedules.map(schedule => (
                    <div key={schedule.id} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        <input
                          type="text"
                          value={schedule.name}
                          onChange={e => handleUpdateCleaningSchedule(schedule.id, 'name', e.target.value)}
                          placeholder="Name"
                          className="w-24 sm:w-28 px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
                        />
                        <select
                          value={schedule.dayOfWeek}
                          onChange={e => handleUpdateCleaningSchedule(schedule.id, 'dayOfWeek', parseInt(e.target.value))}
                          className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
                        >
                          {DAYS.map((day, idx) => (
                            <option key={idx} value={idx}>{day}</option>
                          ))}
                        </select>
                        <div className="flex items-center gap-2">
                          <input
                            type="time"
                            value={schedule.startTime}
                            onChange={e => handleUpdateCleaningSchedule(schedule.id, 'startTime', e.target.value)}
                            className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
                          />
                          <span className="text-gray-500">to</span>
                          <input
                            type="time"
                            value={schedule.endTime}
                            onChange={e => handleUpdateCleaningSchedule(schedule.id, 'endTime', e.target.value)}
                            className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeleteCleaningSchedule(schedule.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded ml-auto sm:ml-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Rooms in Building (only show when editing) */}
            {isEditing && buildingRooms.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Rooms in this Building</h2>
                <div className="divide-y divide-gray-100">
                  {buildingRooms.map(room => (
                    <Link
                      key={room.id}
                      to={`/dashboard/rooms/${room.id}/edit`}
                      className="py-3 flex items-center justify-between hover:bg-gray-50 -mx-2 px-2 rounded"
                    >
                      <div className="flex items-center gap-3">
                        {room.images?.[0] ? (
                          <img src={room.images[0].url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-gray-400" />
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-gray-900">{room.name}</div>
                          <div className="text-sm text-gray-500">Capacity: {room.capacity}</div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
      </div>
    </DashboardLayout>
  )
}
